import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useIdleContext } from "../context/IdleContext";

/** Default: 15 minutes (900000 ms). */
const useIdleTimeout = (timeoutDuration = 900000) => {
  const location = useLocation();
  const idleTimeoutRef = useRef(null);
  const lastResetRef = useRef(0);
  const { triggerIdleTimeout, showIdleModal } = useIdleContext();

  useEffect(() => {
    const clearIdleTimer = () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
    };

    // Pause idle detection while the lock screen is visible
    if (showIdleModal) {
      clearIdleTimer();
      return clearIdleTimer;
    }

    const isAuthPage =
      location.pathname === "/accesspin" ||
      location.pathname === "/login" ||
      location.pathname === "/signup" ||
      location.pathname === "/otp" ||
      location.pathname === "/forgetpassword";

    if (isAuthPage) {
      clearIdleTimer();
      return clearIdleTimer;
    }

    const resetIdleTimer = () => {
      clearIdleTimer();
      lastResetRef.current = Date.now();
      idleTimeoutRef.current = setTimeout(() => {
        triggerIdleTimeout();
      }, timeoutDuration);
    };

    // Throttle high-frequency events (still counts as activity)
    const resetIdleTimerThrottled = () => {
      const now = Date.now();
      if (now - lastResetRef.current < 1000) return;
      resetIdleTimer();
    };

    const throttledEvents = new Set([
      "mousemove",
      "pointermove",
      "touchmove",
      "scroll",
      "wheel",
    ]);

    const activityEvents = [
      "mousedown",
      "mousemove",
      "mouseup",
      "pointerdown",
      "pointermove",
      "keydown",
      "keypress",
      "keyup",
      "input",
      "change",
      "scroll",
      "wheel",
      "touchstart",
      "touchmove",
      "click",
      "focusin",
    ];

    resetIdleTimer();

    const handlers = activityEvents.map((event) => {
      const handler = throttledEvents.has(event)
        ? resetIdleTimerThrottled
        : resetIdleTimer;
      document.addEventListener(event, handler, true);
      return { event, handler };
    });

    return () => {
      clearIdleTimer();
      handlers.forEach(({ event, handler }) => {
        document.removeEventListener(event, handler, true);
      });
    };
  }, [location.pathname, timeoutDuration, showIdleModal, triggerIdleTimeout]);

  return null;
};

export default useIdleTimeout;
